import React, { useState, useMemo } from 'react';

/**
 * LegalReportTab Component
 * Court-ready evidence report with export functionality
 * Displays all verified vehicle data in a format suitable for legal proceedings
 */

// Column definitions with help text for non-technical users
const COLUMN_HELP = {
  licensePlate: {
    label: 'License Plate',
    help: 'The license plate number detected and verified by Google\'s optical character recognition (OCR). This is the text that was actually read from the video frames.'
  },
  vehicle: {
    label: 'Vehicle',
    help: 'Description of the vehicle including make, model, color, and year. This information is extracted from visual analysis of the vehicle in the video.'
  },
  plateReads: {
    label: 'Plate Reads',
    help: 'The number of individual video frames where the license plate was successfully detected and read. More plate reads = stronger evidence because it shows the plate was verified multiple times independently.'
  },
  verifiedTime: {
    label: 'Verified Time',
    help: 'The total duration the license plate was actively readable on screen. This may be shorter than the vehicle\'s total time on screen because plates aren\'t always visible (bad angle, too far away, etc.). "Tracked" time shows additional time where the vehicle was visible but the plate wasn\'t readable.'
  },
  confidence: {
    label: 'Confidence',
    help: 'How certain Google\'s AI is about the plate reading, expressed as a percentage. 95%+ is very reliable. The range shows the lowest and highest confidence across all plate reads for this vehicle.'
  },
  damage: {
    label: 'Damage',
    help: 'Any visible damage to the vehicle detected in the video, such as broken windows, dents, or missing parts. "None" means no obvious damage was observed.'
  }
};

const LegalReportTab = ({ targets = [], analysisData = {}, job = null }) => {
  const [exportStatus, setExportStatus] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Filter to only verified vehicles (PRIMARY anchor with plate detections)
  const verifiedVehicles = useMemo(() => {
    return targets.filter(t => {
      const isVehicle = t.type === 'vehicle' || t.target_type === 'vehicle';
      const isPrimary = t.anchor_type === 'PRIMARY';
      const hasPlate = t.attributes?.license_plate && t.attributes.license_plate !== 'Not determinable';
      return isVehicle && isPrimary && hasPlate;
    });
  }, [targets]);

  // Calculate aggregate statistics
  const aggregateStats = useMemo(() => {
    let totalPlateDetections = 0;
    let totalVerifiedDuration = 0;
    let totalTrackedDuration = 0;
    let allConfidences = [];

    verifiedVehicles.forEach(vehicle => {
      const stats = vehicle.legal_report_stats || {};
      totalPlateDetections += stats.total_plate_detections || 0;
      totalVerifiedDuration += stats.verified_duration || 0;
      totalTrackedDuration += stats.tracked_duration || 0;
      if (stats.confidence_avg) allConfidences.push(stats.confidence_avg);
    });

    return {
      vehicleCount: verifiedVehicles.length,
      totalPlateDetections,
      totalVerifiedDuration: totalVerifiedDuration.toFixed(1),
      totalTrackedDuration: totalTrackedDuration.toFixed(1),
      avgConfidence: allConfidences.length > 0 
        ? (allConfidences.reduce((a, b) => a + b, 0) / allConfidences.length * 100).toFixed(0)
        : 'N/A'
    };
  }, [verifiedVehicles]);

  // Export to CSV
  const exportToCSV = () => {
    setExportStatus('Generating CSV...');
    
    const headers = [
      'Vehicle', 'License Plate', 'Color', 'Make', 'Model', 'Year',
      'Plate Detections', 'Verified Duration (s)', 'Tracked Duration (s)',
      'Confidence Avg', 'Confidence Min', 'Confidence Max',
      'First Seen', 'Last Seen', 'Damage'
    ];

    const rows = verifiedVehicles.map(v => {
      const stats = v.legal_report_stats || {};
      const attrs = v.attributes || {};
      return [
        v.target_id || v.id,
        attrs.license_plate,
        attrs.color,
        attrs.make,
        attrs.model,
        attrs.year,
        stats.total_plate_detections || 0,
        stats.verified_duration?.toFixed(2) || '0',
        stats.tracked_duration?.toFixed(2) || '0',
        stats.confidence_avg ? (stats.confidence_avg * 100).toFixed(1) + '%' : 'N/A',
        stats.confidence_min ? (stats.confidence_min * 100).toFixed(1) + '%' : 'N/A',
        stats.confidence_max ? (stats.confidence_max * 100).toFixed(1) + '%' : 'N/A',
        v.spatial?.first_appearance || v.first_seen || '',
        v.spatial?.last_appearance || v.last_seen || '',
        attrs.damage || 'None'
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    downloadFile(csvContent, 'legal-evidence-report.csv', 'text/csv');
    setExportStatus('CSV exported!');
    setTimeout(() => setExportStatus(null), 2000);
  };

  // Export to JSON
  const exportToJSON = () => {
    setExportStatus('Generating JSON...');
    
    const report = {
      report_type: 'Legal Evidence Report',
      generated_at: new Date().toISOString(),
      job_id: job?.id || 'Unknown',
      summary: {
        total_verified_vehicles: aggregateStats.vehicleCount,
        total_plate_detections: aggregateStats.totalPlateDetections,
        total_verified_duration: aggregateStats.totalVerifiedDuration + 's',
        average_confidence: aggregateStats.avgConfidence + '%'
      },
      vehicles: verifiedVehicles.map(v => ({
        id: v.target_id || v.id,
        attributes: v.attributes,
        legal_report_stats: v.legal_report_stats,
        appearance_segments: v.appearance_segments,
        spatial: v.spatial,
        provenance_log: v.provenance_log
      })),
      methodology: {
        verification_source: 'Google Video Intelligence API',
        plate_detection: 'TEXT_DETECTION (OCR)',
        vehicle_tracking: 'OBJECT_TRACKING',
        matching_algorithm: 'Levenshtein distance fuzzy matching'
      }
    };

    downloadFile(JSON.stringify(report, null, 2), 'legal-evidence-report.json', 'application/json');
    setExportStatus('JSON exported!');
    setTimeout(() => setExportStatus(null), 2000);
  };

  // Export to PDF (simplified text-based for now)
  const exportToPDF = () => {
    setExportStatus('Generating report...');
    
    // Create a printable HTML document
    const printContent = generatePrintableReport();
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    
    setExportStatus('Print dialog opened');
    setTimeout(() => setExportStatus(null), 2000);
  };

  // Generate printable HTML report
  const generatePrintableReport = () => {
    const vehicleRows = verifiedVehicles.map(v => {
      const stats = v.legal_report_stats || {};
      const attrs = v.attributes || {};
      return `
        <tr>
          <td>${attrs.license_plate || 'N/A'}</td>
          <td>${attrs.color || ''} ${attrs.make || ''} ${attrs.model || ''}</td>
          <td>${stats.total_plate_detections || 0}</td>
          <td>${stats.verified_duration?.toFixed(1) || '0'}s</td>
          <td>${stats.confidence_avg ? (stats.confidence_avg * 100).toFixed(0) + '%' : 'N/A'}</td>
          <td>${attrs.damage || 'None'}</td>
        </tr>
      `;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Legal Evidence Report</title>
        <style>
          body { font-family: 'Times New Roman', serif; max-width: 800px; margin: 0 auto; padding: 40px; }
          h1 { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
          h2 { color: #333; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #333; padding: 8px; text-align: left; }
          th { background: #f0f0f0; }
          .summary { background: #f9f9f9; padding: 15px; border: 1px solid #ddd; margin: 20px 0; }
          .methodology { font-size: 0.9em; color: #666; }
          .certification { margin-top: 40px; padding-top: 20px; border-top: 1px solid #333; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>📋 LEGAL EVIDENCE REPORT</h1>
        <p style="text-align: center; color: #666;">
          Generated: ${new Date().toLocaleString()}<br/>
          Job ID: ${job?.id || 'N/A'}
        </p>
        
        <div class="summary">
          <h3>Evidence Summary</h3>
          <ul>
            <li><strong>Verified Vehicles:</strong> ${aggregateStats.vehicleCount}</li>
            <li><strong>Total Plate Detections:</strong> ${aggregateStats.totalPlateDetections}</li>
            <li><strong>Total Verified Duration:</strong> ${aggregateStats.totalVerifiedDuration}s</li>
            <li><strong>Average Confidence:</strong> ${aggregateStats.avgConfidence}%</li>
          </ul>
        </div>

        <h2>Vehicle Evidence</h2>
        <table>
          <thead>
            <tr>
              <th>License Plate</th>
              <th>Vehicle Description</th>
              <th>Plate Detections</th>
              <th>Verified Duration</th>
              <th>Confidence</th>
              <th>Damage</th>
            </tr>
          </thead>
          <tbody>
            ${vehicleRows}
          </tbody>
        </table>

        <h2 class="methodology">Methodology</h2>
        <p class="methodology">
          All license plate detections were verified using Google's Video Intelligence API 
          TEXT_DETECTION feature (OCR). Vehicle tracking was performed using OBJECT_TRACKING. 
          Plate-to-vehicle matching used Levenshtein distance fuzzy matching algorithm.
        </p>
        <p class="methodology">
          <strong>Verification Tiers:</strong><br/>
          • VERIFIED: Plate detected by Google's OCR at specific timestamp<br/>
          • TRACKED: Vehicle visible but plate not readable (contextual only)
        </p>

        <div class="certification">
          <h3>Certification</h3>
          <p>
            I certify that this report accurately reflects the data extracted from the 
            submitted video evidence using automated computer vision analysis. All plate 
            detections shown as "VERIFIED" were confirmed by Google's Video Intelligence 
            API with the confidence scores indicated.
          </p>
          <p style="margin-top: 30px;">
            Signature: ______________________________ Date: ______________
          </p>
        </div>
      </body>
      </html>
    `;
  };

  // Helper to download file
  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: 'var(--space-3)' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.05))',
        border: '1px solid rgba(34, 197, 94, 0.3)',
        borderRadius: '12px',
        padding: 'var(--space-4)',
        marginBottom: 'var(--space-4)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
          <span style={{ fontSize: '32px' }}>⚖️</span>
          <div>
            <h2 style={{ 
              margin: 0, 
              fontSize: 'var(--text-xl)', 
              fontWeight: 'var(--font-bold)',
              color: 'var(--text-primary)'
            }}>
              Legal Evidence Report
            </h2>
            <p style={{ 
              margin: '4px 0 0 0', 
              fontSize: 'var(--text-sm)', 
              color: 'var(--text-secondary)' 
            }}>
              Court-ready vehicle identification evidence
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 'var(--space-3)'
        }}>
          <StatCard 
            label="Verified Vehicles" 
            value={aggregateStats.vehicleCount} 
            icon="🚗"
            help="Vehicles with license plates that were successfully read and verified by Google's AI. Only vehicles with confirmed plate readings appear in this report."
          />
          <StatCard 
            label="Plate Detections" 
            value={aggregateStats.totalPlateDetections} 
            icon="📋"
            help="Total number of individual frames where license plates were detected across all vehicles. Each detection is an independent verification by Google's OCR system."
          />
          <StatCard 
            label="Verified Duration" 
            value={`${aggregateStats.totalVerifiedDuration}s`} 
            icon="⏱️"
            help="Total time that license plates were actively readable on screen. This is the 'provable' time - we can point to exact frames showing the plate."
          />
          <StatCard 
            label="Avg Confidence" 
            value={`${aggregateStats.avgConfidence}%`} 
            icon="✓"
            help="Average certainty of plate readings. 90%+ means Google's AI is very confident the plate was read correctly. Higher is better for legal evidence."
          />
        </div>
      </div>

      {/* Export Buttons */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-4)',
        flexWrap: 'wrap'
      }}>
        <ExportButton onClick={exportToPDF} icon="📄" label="Print / PDF" />
        <ExportButton onClick={exportToCSV} icon="📊" label="Export CSV" />
        <ExportButton onClick={exportToJSON} icon="{}" label="Export JSON" />
        {exportStatus && (
          <span style={{ 
            padding: 'var(--space-2) var(--space-3)',
            background: 'rgba(34, 197, 94, 0.2)',
            color: '#22c55e',
            borderRadius: '8px',
            fontSize: 'var(--text-sm)'
          }}>
            {exportStatus}
          </span>
        )}
      </div>

      {/* Vehicle Evidence Table */}
      <div style={{
        background: 'var(--bg-card)',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: 'var(--space-3)',
          background: 'var(--bg-secondary)',
          borderBottom: '1px solid var(--border)'
        }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: 'var(--text-base)', 
            fontWeight: 'var(--font-semibold)',
            color: 'var(--text-primary)'
          }}>
            📋 Verified Vehicle Evidence
          </h3>
        </div>

        {verifiedVehicles.length === 0 ? (
          <div style={{
            padding: 'var(--space-6)',
            textAlign: 'center',
            color: 'var(--text-muted)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: 'var(--space-2)' }}>🔍</div>
            <p>No verified vehicles found in this analysis.</p>
            <p style={{ fontSize: 'var(--text-sm)', opacity: 0.7 }}>
              Vehicles require a readable license plate (PRIMARY anchor) to appear in the legal report.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: 'var(--text-sm)'
            }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)' }}>
                  <th style={thStyle}>
                    <ColumnHeader 
                      columnKey="licensePlate" 
                      activeTooltip={activeTooltip} 
                      setActiveTooltip={setActiveTooltip} 
                    />
                  </th>
                  <th style={thStyle}>
                    <ColumnHeader 
                      columnKey="vehicle" 
                      activeTooltip={activeTooltip} 
                      setActiveTooltip={setActiveTooltip} 
                    />
                  </th>
                  <th style={thStyle}>
                    <ColumnHeader 
                      columnKey="plateReads" 
                      activeTooltip={activeTooltip} 
                      setActiveTooltip={setActiveTooltip} 
                    />
                  </th>
                  <th style={thStyle}>
                    <ColumnHeader 
                      columnKey="verifiedTime" 
                      activeTooltip={activeTooltip} 
                      setActiveTooltip={setActiveTooltip} 
                    />
                  </th>
                  <th style={thStyle}>
                    <ColumnHeader 
                      columnKey="confidence" 
                      activeTooltip={activeTooltip} 
                      setActiveTooltip={setActiveTooltip} 
                    />
                  </th>
                  <th style={thStyle}>
                    <ColumnHeader 
                      columnKey="damage" 
                      activeTooltip={activeTooltip} 
                      setActiveTooltip={setActiveTooltip} 
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {verifiedVehicles.map((vehicle, idx) => (
                  <VehicleRow key={vehicle.target_id || idx} vehicle={vehicle} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Methodology Section */}
      <div style={{
        marginTop: 'var(--space-4)',
        padding: 'var(--space-4)',
        background: 'var(--bg-card)',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}>
        <h3 style={{ 
          margin: '0 0 var(--space-3) 0', 
          fontSize: 'var(--text-base)', 
          fontWeight: 'var(--font-semibold)',
          color: 'var(--text-primary)'
        }}>
          📝 Methodology
        </h3>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 var(--space-2) 0' }}>
            <strong>Verification Source:</strong> Google Video Intelligence API
          </p>
          <p style={{ margin: '0 0 var(--space-2) 0' }}>
            <strong>Plate Detection:</strong> TEXT_DETECTION (OCR) - identifies and reads license plates
          </p>
          <p style={{ margin: '0 0 var(--space-2) 0' }}>
            <strong>Vehicle Tracking:</strong> OBJECT_TRACKING - tracks vehicle positions across frames
          </p>
          <p style={{ margin: '0 0 var(--space-2) 0' }}>
            <strong>Matching Algorithm:</strong> Levenshtein distance fuzzy matching for plate-to-vehicle association
          </p>
          <div style={{
            marginTop: 'var(--space-3)',
            padding: 'var(--space-3)',
            background: 'rgba(34, 197, 94, 0.1)',
            borderRadius: '8px',
            border: '1px solid rgba(34, 197, 94, 0.2)'
          }}>
            <strong style={{ color: '#22c55e' }}>✓ Suitable for Court Testimony</strong>
            <p style={{ margin: '8px 0 0 0', fontSize: 'var(--text-xs)' }}>
              All "VERIFIED" plate detections were confirmed by Google's Video Intelligence API, 
              a deterministic computer vision system. Each detection includes exact timestamp, 
              bounding box coordinates, and confidence score.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Column Header with Help Tooltip
const ColumnHeader = ({ columnKey, activeTooltip, setActiveTooltip }) => {
  const column = COLUMN_HELP[columnKey];
  const isActive = activeTooltip === columnKey;
  
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span>{column.label}</span>
      <button
        onClick={() => setActiveTooltip(isActive ? null : columnKey)}
        onMouseEnter={() => setActiveTooltip(columnKey)}
        onMouseLeave={() => setActiveTooltip(null)}
        style={{
          background: isActive ? 'var(--accent)' : 'rgba(255, 255, 255, 0.1)',
          border: 'none',
          borderRadius: '50%',
          width: '16px',
          height: '16px',
          fontSize: '10px',
          color: isActive ? 'white' : 'var(--text-muted)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s',
          fontWeight: 'bold'
        }}
        aria-label={`Help: ${column.label}`}
      >
        ?
      </button>
      
      {/* Tooltip */}
      {isActive && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          background: '#1e1e2e',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '12px 14px',
          width: '260px',
          maxWidth: '90vw',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
          zIndex: 1000,
          fontSize: '12px',
          fontWeight: 'normal',
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
          textAlign: 'left',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal'
        }}>
          <div style={{ 
            fontWeight: '600', 
            color: 'var(--text-primary)',
            marginBottom: '6px',
            fontSize: '13px'
          }}>
            {column.label}
          </div>
          <div style={{ color: '#a0a0b0' }}>
            {column.help}
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component with optional help tooltip
const StatCard = ({ label, value, icon, help }) => {
  const [showHelp, setShowHelp] = useState(false);
  
  return (
    <div style={{
      background: 'var(--bg-card)',
      padding: 'var(--space-3)',
      borderRadius: '8px',
      border: '1px solid var(--border)',
      textAlign: 'center',
      position: 'relative'
    }}>
      <div style={{ fontSize: '24px', marginBottom: 'var(--space-1)' }}>{icon}</div>
      <div style={{ 
        fontSize: 'var(--text-xl)', 
        fontWeight: 'var(--font-bold)',
        color: 'var(--text-primary)'
      }}>
        {value}
      </div>
      <div style={{ 
        fontSize: 'var(--text-xs)', 
        color: 'var(--text-muted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px'
      }}>
        {label}
        {help && (
          <button
            onClick={() => setShowHelp(!showHelp)}
            onMouseEnter={() => setShowHelp(true)}
            onMouseLeave={() => setShowHelp(false)}
            style={{
              background: showHelp ? 'var(--accent)' : 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '14px',
              height: '14px',
              fontSize: '9px',
              color: showHelp ? 'white' : 'var(--text-muted)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              flexShrink: 0
            }}
            aria-label={`Help: ${label}`}
          >
            ?
          </button>
        )}
      </div>
      
      {/* Help Tooltip */}
      {help && showHelp && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          background: '#1e1e2e',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '12px 14px',
          width: '220px',
          maxWidth: '90vw',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
          zIndex: 1000,
          fontSize: '12px',
          color: '#a0a0b0',
          lineHeight: 1.6,
          textAlign: 'left',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal'
        }}>
          {help}
        </div>
      )}
    </div>
  );
};

// Export Button Component
const ExportButton = ({ onClick, icon, label }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      padding: 'var(--space-3) var(--space-4)',
      background: 'var(--bg-card)',
      color: 'var(--text-primary)',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      fontSize: 'var(--text-sm)',
      fontWeight: 'var(--font-semibold)',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'var(--accent)';
      e.currentTarget.style.borderColor = 'var(--accent)';
      e.currentTarget.style.color = 'white';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'var(--bg-card)';
      e.currentTarget.style.borderColor = 'var(--border)';
      e.currentTarget.style.color = 'var(--text-primary)';
    }}
  >
    <span>{icon}</span>
    <span>{label}</span>
  </button>
);

// Vehicle Row Component
const VehicleRow = ({ vehicle }) => {
  const attrs = vehicle.attributes || {};
  const stats = vehicle.legal_report_stats || {};
  const spatial = vehicle.spatial || {};

  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={tdStyle}>
        <span style={{
          background: 'rgba(34, 197, 94, 0.2)',
          color: '#22c55e',
          padding: '4px 8px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontWeight: 'bold'
        }}>
          {attrs.license_plate}
        </span>
      </td>
      <td style={tdStyle}>
        <div style={{ fontWeight: 'var(--font-semibold)' }}>
          {attrs.color} {attrs.make} {attrs.model}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {attrs.year} • {attrs.vehicle_type}
        </div>
      </td>
      <td style={tdStyle}>
        <span style={{ 
          fontWeight: 'var(--font-bold)',
          color: '#22c55e'
        }}>
          {stats.total_plate_detections || 0}
        </span>
      </td>
      <td style={tdStyle}>
        {stats.verified_duration?.toFixed(1) || '0'}s verified
        {stats.tracked_duration > 0 && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            +{stats.tracked_duration?.toFixed(1)}s tracked
          </div>
        )}
      </td>
      <td style={tdStyle}>
        <div>
          {stats.confidence_avg ? `${(stats.confidence_avg * 100).toFixed(0)}%` : 'N/A'}
        </div>
        {stats.confidence_min && stats.confidence_max && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
            {(stats.confidence_min * 100).toFixed(0)}% - {(stats.confidence_max * 100).toFixed(0)}%
          </div>
        )}
      </td>
      <td style={tdStyle}>
        {attrs.damage && attrs.damage !== 'None' && attrs.damage !== 'No visible damage' && attrs.damage !== 'Not determinable' ? (
          <span style={{ color: '#f59e0b' }}>⚠️ {attrs.damage}</span>
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>None</span>
        )}
      </td>
    </tr>
  );
};

// Table styles
const thStyle = {
  padding: 'var(--space-3)',
  textAlign: 'left',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--text-secondary)',
  borderBottom: '2px solid var(--border)',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: 'var(--space-3)',
  verticalAlign: 'top'
};

export default LegalReportTab;

